import { State } from "..";

import { AbstractStore } from ".";

/**
 * Possible noise suppresion states. Browser is browser noise suppresion,
 * enhanced is machine learning suppression via RNNoise, and auto is RNNoise
 * plus a level control that follows the room (see rtc/audio).
 */
export type NoiseSuppresionState =
  | "disabled"
  | "browser"
  | "enhanced"
  | "auto";

const NoiseSuppresionStates: NoiseSuppresionState[] = [
  "disabled",
  "browser",
  "enhanced",
  "auto",
];

/**
 * Possible screen share qualities. Low is 720p@30fps, high 1080p@30fps and text is source@5fps.
 */
export type ScreenShareQualityName = "low" | "high" | "text";

/**
 * Array of available screen share quality names.
 */
export const ScreenShareQualityNames: ScreenShareQualityName[] = [
  "low",
  "high",
  "text",
];

export interface TypeVoice {
  preferredAudioInputDevice?: string;
  preferredAudioOutputDevice?: string;
  /**
   * Названия выбранных устройств.
   *
   * `deviceId` — соль браузера, а не свойство железа: чистка данных сайта,
   * отзыв разрешения и (на части систем) переподключение USB-гарнитуры дают
   * новый id тому же устройству. Название переживает всё это и позволяет
   * вернуть человеку именно его микрофон, а не «системный по умолчанию».
   */
  preferredAudioInputLabel?: string;
  preferredAudioOutputLabel?: string;

  echoCancellation: boolean;
  noiseSupression: NoiseSuppresionState;
  autoGainControl: boolean;
  /**
   * Whether this profile has already been moved onto the automatic mode.
   *
   * Everyone currently sits on "browser" because that used to be the default,
   * not because anyone chose it — a new default alone would never reach them.
   * The flag makes the move happen exactly once and leaves every later choice
   * (including going back) untouched.
   */
  noiseSupressionAutoMigrated: boolean;
  /**
   * Whether the speech threshold is derived from the room (default) or set by
   * hand. Manual exists for rooms automatic tracking cannot win: an open
   * office, a TV in the background — places where the noise is itself speech.
   */
  micSensitivityAuto: boolean;
  /** Hand-set threshold in dBFS; ignored while `micSensitivityAuto` is on. */
  micSensitivityDb: number;

  screenShareQuality: ScreenShareQualityName;
  screenShareQualityAsk: boolean;

  inputVolume: number;
  outputVolume: number;
  deafen: boolean;
  micOn: boolean;

  userVolumes: Record<string, number>;
  userMutes: Record<string, boolean>;
}

/**
 * Handles enabling and disabling client experiments.
 */
export class Voice extends AbstractStore<"voice", TypeVoice> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "voice");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeVoice {
    return {
      echoCancellation: true,
      noiseSupression: "auto",
      autoGainControl: true,
      noiseSupressionAutoMigrated: true,
      micSensitivityAuto: true,
      micSensitivityDb: -45,
      screenShareQuality: "high",
      screenShareQualityAsk: true,
      inputVolume: 1.0,
      outputVolume: 1.0,
      deafen: false,
      micOn: true,
      userVolumes: {},
      userMutes: {},
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeVoice>): TypeVoice {
    const data = this.default();

    if (typeof input.preferredAudioInputDevice === "string") {
      data.preferredAudioInputDevice = input.preferredAudioInputDevice;
    }

    if (typeof input.preferredAudioOutputDevice === "string") {
      data.preferredAudioOutputDevice = input.preferredAudioOutputDevice;
    }

    if (typeof input.preferredAudioInputLabel === "string") {
      data.preferredAudioInputLabel = input.preferredAudioInputLabel;
    }

    if (typeof input.preferredAudioOutputLabel === "string") {
      data.preferredAudioOutputLabel = input.preferredAudioOutputLabel;
    }

    if (typeof input.echoCancellation === "boolean") {
      data.echoCancellation = input.echoCancellation;
    }

    // migrate legacy noise suppression to new suppression state
    if ((input.noiseSupression as unknown) === "true") {
      data.noiseSupression = "browser";
    } else if ((input.noiseSupression as unknown) === "false") {
      data.noiseSupression = "disabled";
    } else if (
      input.noiseSupression &&
      NoiseSuppresionStates.includes(input.noiseSupression)
    ) {
      data.noiseSupression = input.noiseSupression;
    }

    if (typeof input.autoGainControl === "boolean") {
      data.autoGainControl = input.autoGainControl;
    }

    if (typeof input.micSensitivityAuto === "boolean") {
      data.micSensitivityAuto = input.micSensitivityAuto;
    }

    if (
      typeof input.micSensitivityDb === "number" &&
      input.micSensitivityDb >= -90 &&
      input.micSensitivityDb <= 0
    ) {
      data.micSensitivityDb = input.micSensitivityDb;
    }

    // One-off move of existing profiles onto the automatic mode; see the field.
    if (input.noiseSupressionAutoMigrated === true) {
      data.noiseSupressionAutoMigrated = true;
    } else {
      data.noiseSupression = "auto";
      data.noiseSupressionAutoMigrated = true;
    }

    if (
      input.screenShareQuality &&
      ScreenShareQualityNames.includes(input.screenShareQuality)
    ) {
      data.screenShareQuality = input.screenShareQuality;
    }

    if (typeof input.screenShareQualityAsk === "boolean") {
      data.screenShareQualityAsk = input.screenShareQualityAsk;
    }

    if (typeof input.inputVolume === "number") {
      data.inputVolume = input.inputVolume;
    }

    if (typeof input.outputVolume === "number") {
      data.outputVolume = input.outputVolume;
    }

    if (typeof input.deafen === "boolean") {
      data.deafen = input.deafen;
    }

    if (typeof input.micOn === "boolean") {
      data.micOn = input.micOn;
    }

    if (typeof input.userVolumes === "object") {
      Object.entries(input.userVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" && typeof volume === "number",
        )
        .forEach(([k, v]) => (data.userVolumes[k] = v));
    }

    if (typeof input.userMutes === "object") {
      Object.entries(input.userMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.userMutes[k] = v));
    }

    return data;
  }

  /**
   * Set a user's volume
   * @param userId User ID
   * @param volume Volume
   */
  setUserVolume(userId: string, volume: number) {
    this.set("userVolumes", userId, volume);
  }

  /**
   * Get a user's volume
   * @param userId User ID
   * @returns Volume or default
   */
  getUserVolume(userId: string): number {
    return this.get().userVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setUserMuted(userId: string, muted: boolean) {
    this.set("userMutes", userId, muted);
  }

  /**
   * Get whether a user is muted
   * @param userId User ID
   * @returns Whether muted
   */
  getUserMuted(userId: string): boolean {
    return this.get().userMutes[userId] || false;
  }

  /**
   * Set the preferred audio input device
   */
  set preferredAudioInputLabel(value: string) {
    this.set("preferredAudioInputLabel", value);
  }

  set preferredAudioOutputLabel(value: string) {
    this.set("preferredAudioOutputLabel", value);
  }

  set preferredAudioInputDevice(value: string) {
    this.set("preferredAudioInputDevice", value);
  }

  /**
   * Set the preferred audio output device
   */
  set preferredAudioOutputDevice(value: string) {
    this.set("preferredAudioOutputDevice", value);
  }

  /**
   * Set echo cancellation
   */
  set echoCancellation(value: boolean) {
    this.set("echoCancellation", value);
  }

  /**
   * Set noise cancellation
   */
  set micSensitivityAuto(value: boolean) {
    this.set("micSensitivityAuto", value);
  }

  set micSensitivityDb(value: number) {
    this.set("micSensitivityDb", Math.min(0, Math.max(-90, value)));
  }

  set noiseSupression(value: NoiseSuppresionState) {
    this.set("noiseSupressionAutoMigrated", true);
    this.set("noiseSupression", value);
  }

  /**
   * Set auto gain control
   */
  set autoGainControl(value: boolean) {
    this.set("autoGainControl", value);
  }

  /**
   * Set screen share quality
   */
  set screenShareQuality(value: ScreenShareQualityName) {
    this.set("screenShareQuality", value);
  }

  /**
   * Set screen share quality always ask
   */
  set screenShareQualityAsk(value: boolean) {
    this.set("screenShareQualityAsk", value);
  }

  /**
   * Set input volume
   */
  set inputVolume(value: number) {
    this.set("inputVolume", value);
  }

  /**
   * Set output volume
   */
  set outputVolume(value: number) {
    this.set("outputVolume", value);
  }

  /**
   * Set mic status
   */
  set micOn(value: boolean) {
    this.set("micOn", value);
  }

  /**
   * Set defean status
   */
  set deafen(value: boolean) {
    this.set("deafen", value);
  }

  /**
   * Get the preferred audio input device
   */
  get preferredAudioInputDevice(): string | undefined {
    return this.get().preferredAudioInputDevice;
  }

  /**
   * Get the preferred audio output device
   */
  get preferredAudioOutputDevice(): string | undefined {
    return this.get().preferredAudioOutputDevice;
  }

  /**
   * Названия выбранных устройств — запасной ключ поиска, см. поле
   */
  get preferredAudioInputLabel(): string | undefined {
    return this.get().preferredAudioInputLabel;
  }

  get preferredAudioOutputLabel(): string | undefined {
    return this.get().preferredAudioOutputLabel;
  }

  /**
   * Get echo cancellation
   */
  get echoCancellation(): boolean | undefined {
    return this.get().echoCancellation;
  }

  /**
   * Get noise supression
   */
  get noiseSupression(): NoiseSuppresionState | undefined {
    return this.get().noiseSupression;
  }

  /**
   * Get auto gain control
   */
  get autoGainControl(): boolean | undefined {
    return this.get().autoGainControl;
  }

  /**
   * Whether the speech threshold follows the room
   */
  get micSensitivityAuto(): boolean {
    return this.get().micSensitivityAuto ?? true;
  }

  /**
   * Hand-set speech threshold, dBFS
   */
  get micSensitivityDb(): number {
    return this.get().micSensitivityDb ?? -45;
  }

  /**
   * Get screen share quality
   */
  get screenShareQuality(): ScreenShareQualityName | undefined {
    return this.get().screenShareQuality;
  }

  /**
   * Get screen share quality always ask
   */
  get screenShareQualityAsk(): boolean {
    return this.get().screenShareQualityAsk;
  }

  /**
   * Get input volume
   */
  get inputVolume(): number {
    return this.get().inputVolume;
  }

  /**
   * Get output volume
   */
  get outputVolume(): number {
    return this.get().outputVolume;
  }

  /**
   * Get deafen status
   */
  get deafen(): boolean {
    return this.get().deafen;
  }

  /**
   * Get mic status
   */
  get micOn(): boolean {
    return this.get().micOn;
  }
}
